import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, mediaUrl, money, apiErr } from "@/lib/api";
import { Logo, Badge, Button } from "@/lib/ui";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [tab, setTab] = useState("catalogue");
  const [ack, setAck] = useState(false);
  const [disc, setDisc] = useState([]);

  useEffect(() => {
    api.get(`/marketplace/products/${id}`).then(({ data }) => setP(data)).catch(() => nav("/marketplace"));
    api.get("/public/disclaimers", { params: { location: "Product Detail" } }).then(({ data }) => setDisc(data)).catch(() => {});
  }, [id, nav]);

  const connect = () => {
    if (!ack) { toast.error("Please agree to the Limitation of Liability to continue"); return; }
    if (!p.whatsapp) { toast.error("Seller contact unavailable"); return; }
    const text = encodeURIComponent(`Hi, I saw your stock at www.deadstock.live — I'm interested in your "${p.product}" (${p.size}) listed at ${money(p.primary_rate)}/${p.primary_unit}.`);
    // Open synchronously within the click gesture so the browser does not block the popup.
    window.open(`https://wa.me/91${p.whatsapp}?text=${text}`, "_blank");
    // Log the click for admin proof (fire-and-forget, does not block WhatsApp).
    api.post(`/marketplace/products/${id}/whatsapp-click`).catch(() => {});
  };

  if (!p) return <div className="flex h-screen items-center justify-center text-slate-400">Loading…</div>;

  const media = {
    catalogue: p.catalogue_image,
    stock: p.stock_image,
    video: p.stock_video,
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3">
          <button onClick={() => nav("/marketplace")} className="text-slate-500 hover:text-slate-900" data-testid="back-to-marketplace">
            <i className="fa-solid fa-chevron-left mr-1" /> Marketplace
          </button>
          <div className="ml-auto"><Logo /></div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-6">
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Media */}
          <div>
            <div className="aspect-square overflow-hidden rounded-xl border border-slate-200 bg-white">
              {tab === "video" ? (
                media.video ? (
                  <video src={mediaUrl(media.video)} controls className="h-full w-full object-cover" data-testid="stock-video-player" />
                ) : (
                  <div className="flex h-full flex-col items-center justify-center text-slate-400">
                    <i className="fa-solid fa-video-slash text-3xl" />
                    <p className="mt-2 text-sm">Video not provided for this demo listing</p>
                  </div>
                )
              ) : (
                <img src={mediaUrl(media[tab])} alt={p.product} className="h-full w-full object-cover" />
              )}
            </div>
            <div className="mt-3 flex gap-2">
              {[
                { k: "catalogue", t: "Catalogue", i: "fa-image" },
                { k: "stock", t: "Actual Stock", i: "fa-camera" },
                { k: "video", t: "Real Stock Video", i: "fa-video" },
              ].map((m) => (
                <button
                  key={m.k}
                  data-testid={`media-tab-${m.k}`}
                  onClick={() => setTab(m.k)}
                  className={`flex-1 rounded-lg border px-3 py-2 text-xs font-semibold transition ${
                    tab === m.k ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
                  }`}
                >
                  <i className={`fa-solid ${m.i} mr-1`} /> {m.t}
                </button>
              ))}
            </div>
          </div>

          {/* Info */}
          <div>
            <div className="flex items-center gap-2">
              <Badge tone="slate">{p.category}</Badge>
              {p.pan_india && <Badge tone="orange"><i className="fa-solid fa-truck-fast" /> Available Pan India</Badge>}
              {p.manufacturer_stock && <Badge tone="blue">Manufacturer Stock</Badge>}
            </div>
            <h1 className="mt-3 font-head text-2xl font-extrabold text-slate-900 sm:text-3xl">{p.product}</h1>
            <p className="mt-1 text-sm text-slate-500">{p.size} · {p.condition}{p.quality ? ` · ${p.quality} Quality` : ""}</p>
            {p.description && <p data-testid="product-description" className="mt-3 whitespace-pre-line rounded-xl bg-slate-50 p-3 text-sm leading-relaxed text-slate-600">{p.description}</p>}

            <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-end gap-3">
                <span className="font-head text-3xl font-extrabold text-slate-900">{money(p.primary_rate)}</span>
                <span className="text-sm text-slate-400">/ {p.primary_unit}</span>
                {p.discount > 0 && <Badge tone="orange" className="mb-1 text-sm">{p.discount}% OFF</Badge>}
              </div>
              {p.actual_rate > p.primary_rate && (
                <p className="strike text-sm text-slate-400">{money(p.actual_rate)} / {p.primary_unit}</p>
              )}
              {p.secondary_unit && p.secondary_rate > 0 && (
                <p className="mt-1 text-sm font-semibold text-slate-700">{money(p.secondary_rate)} / {p.secondary_unit}</p>
              )}
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <Info label="Available" value={`${p.quantity} ${p.primary_unit}s`} />
              <Info label="MOQ" value={p.moq ? `${p.moq} ${p.primary_unit}s` : "No MOQ"} />
              <Info label="Condition" value={p.condition} />
              <Info label="Quality" value={p.quality || "—"} />
              <Info label="Stock Location" value={`${p.locality}, ${p.city}`} />
              <Info label="Seller" value={<span>{p.company_name} {p.seller_badge && <i className="fa-solid fa-circle-check text-emerald-600" />}</span>} />
            </div>

            {disc.map((d) => (
              <p key={d.id} className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <i className="fa-solid fa-circle-info mr-1" /> {d.text}
              </p>
            ))}

            <div className="mt-4 rounded-xl border border-slate-300 bg-slate-50 p-3" data-testid="liability-box">
              <p className="mb-1 font-head text-sm font-bold text-slate-800"><i className="fa-solid fa-scale-balanced mr-1 text-slate-500" />Limitation of Liability</p>
              <p className="text-[11px] leading-relaxed text-slate-500">
                Deadstock.live shall not be responsible or liable for any fraud, misrepresentation, breach of contract, non-payment, non-delivery, counterfeit or unauthorised goods, or any other dispute, loss, or claim arising directly from or in connection with a transaction between a Buyer and a Seller.
              </p>
              <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
                The Buyer and Seller shall be solely responsible for conducting appropriate due diligence, verifying the identity and credentials of the other party, confirming the authenticity and condition of goods, and ensuring the accuracy and legality of the transaction. Any dispute arising between the Buyer and Seller shall be resolved directly between the concerned parties.
              </p>
              <label className="mt-3 flex items-start gap-2 text-xs font-semibold text-slate-700">
                <input data-testid="whatsapp-ack-checkbox" type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5" />
                I have read and agree to the Limitation of Liability above.
              </label>
            </div>

            <Button data-testid="whatsapp-seller-cta" variant="green" onClick={connect} className="mt-3 w-full py-3 text-base">
              <i className="fa-brands fa-whatsapp text-lg" /> WhatsApp Seller
            </Button>

            <p data-testid="whatsapp-disclaimer" className="mt-2 text-center text-xs text-slate-400">
              deadstock.live just connects buyer and seller and does not guarantee anything else
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</p>
      <p className="mt-0.5 font-semibold text-slate-800">{value}</p>
    </div>
  );
}
